import { useCallback, useEffect, useState } from 'react';
import { InteractionManager } from 'react-native';
import {
  HeaderIcon,
  NaviIconHome,
  StackScreen,
  useStatusBarUpdate,
} from '@banhala/ui-kit';
import { GoodsDetailContentsSkeleton } from './components/GoodsDetailContentsSkeleton';
import { useQueryClient } from '@tanstack/react-query';
import { useHandleBackPressed, useIsForegroundNow } from 'shared/src/hooks';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { getNumberRange, isNotNil, useNavigator } from 'shared/src/utils';
import { AnalyticsScreen } from 'shared/src/context';
import type { ILogging } from 'shared/src/api/types/ILogging';
import { goodsService } from 'shared/src/api/goodsService';
import { GoodsDetailContents } from './components/GoodsDetailContents';
import { useLogClickHome } from './hooks/useLogClickHome';
import { useAffiliateAccessLog } from './hooks/useAffiliateAccessLog';
import { useTrackingLinkAccessLog } from './hooks/useTrackingLinkAccessLog';
import { BottomDockedHeightProvider } from 'shared/src/hooks/useBottomDockedHeight';
import { useGoodsSno } from './hooks/useGoodsSno';
import { useFixedCouponSno } from './hooks/useFixedCouponSno';
import { queryKey } from './constant/queryKey';
import { useEntryAdInfo } from './hooks/useEntryAdInfo';
import { useGoodsFrom } from './hooks/useGoodsFrom';
import { useStopSaleGuard } from './hooks/useStopSaleGuard';
import { usePreloadQuery } from './hooks/usePreloadQuery';
import { useSameMarketReentry } from './hooks/useSameMarketReentry';
import { useIsPostGoodsActionBottomSheetOpen } from './atom/isPostGoodsActionBottomSheetOpen.atom';

type GoodsDetailData = Awaited<ReturnType<typeof goodsService.getGoodsDetail>>;
type GoodsBasicData = Awaited<ReturnType<typeof goodsService.getGoodsBasic>>;

/**
 * 상세 화면의 "컨트롤러 + 뷰" 본체. InactiveScreenGate 아래(AnalyticsScreen 의 children)로
 * 렌더되어, 화면이 비활성 depth 로 unmount 될 때 여기의 훅(하드웨어 back 핸들러·상태바·
 * 네이티브 이벤트 구독·stop-sale 가드·click-home·컨텐츠)이 함께 해제된다.
 *
 * ⚠️ 게이트 "위"(GoodsDetailPage)에 남겨야 하는 것만 밖에 둔다:
 * - getDefaults 및 그 피더(goodsSno/couponSno/from/entryAd): AnalyticsProvider·ModalProvider
 *   가 게이트 밖이라, 모달발 이벤트까지 기본 로깅 필드를 받으려면 위에 있어야 한다.
 * - usePreloadQuery: 내부 useConfigs() 가 suspense 라, 게이트(Freeze/Suspense) 안에서
 *   suspend 되면 상위 LoadingView 대신 게이트 placeholder(blank)로 잡힌다. 진입 prefetch 는
 *   방문당 1회 top-level 로 도는 게 맞으므로 shell 에 유지.
 */
const GoodsDetailBody = () => {
  const { navigator } = useNavigator();

  const isForeground = useIsForegroundNow();
  const [isPostGoodsActionBottomSheetOpen] =
    useIsPostGoodsActionBottomSheetOpen();

  const { onPressBack, showBackButton } = useHandleBackPressed({
    shouldSkip: isForeground && isPostGoodsActionBottomSheetOpen,
  });

  useStopSaleGuard();
  // warm 서피스 재사용으로 같은 goods 재진입 시 새 딥링크의 same_market_recommend_only 반영
  useSameMarketReentry();
  useAffiliateAccessLog();
  useTrackingLinkAccessLog();
  const logClickHome = useLogClickHome();

  useStatusBarUpdate('white');

  // push 애니메이션이 끝난(=인터랙션 종료) 뒤에 무거운 GoodsDetailContents 를 마운트한다.
  // 그 전까지는 가벼운 스켈레톤만 그려, 첫 commit 을 가볍게 유지 → 전환이 즉시 시작.
  // (콘텐츠를 전환 도중 마운트하면 그 커밋이 push 슬라이드 시작을 밀어 push 가 늦어진다.
  //  대신 콘텐츠가 뜬 뒤의 첫 커밋 크기는 initialNumToRender 로 줄여 커버를 빨리 페인트한다.)
  const [contentReady, setContentReady] = useState(false);
  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      setContentReady(true);
    });
    return () => task.cancel();
  }, []);

  const handleHomeClick = useCallback(() => {
    logClickHome();
    navigator.moveToHome();
  }, [logClickHome, navigator]);

  return (
    <BottomDockedHeightProvider>
      <StackScreen
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        backButtonTestID={'뒤로가기버튼'}
        rightIcons={[
          <HeaderIcon testID={'홈버튼'} onPress={handleHomeClick}>
            <NaviIconHome />
          </HeaderIcon>,
          <SearchIcon />,
          <CartIcon />,
        ]}
      >
        {contentReady ? (
          <GoodsDetailContents />
        ) : (
          <GoodsDetailContentsSkeleton />
        )}
      </StackScreen>
    </BottomDockedHeightProvider>
  );
};

const GoodsDetailPage = () => {
  const queryClient = useQueryClient();
  const goodsSno = useGoodsSno();
  const fixedCouponSno = useFixedCouponSno();
  // 상세 진입 출처 화면(FROM). 네이티브 상세와 달리 RN 상세는 자동 부착이 없어
  // getDefaults에 넣어 모든 이벤트(cart/like 포함)에 전파한다.
  const from = useGoodsFrom();
  const entryAd = useEntryAdInfo();

  // 진입 즉시 critical 쿼리(basic/detail) prefetch 시작 → 네트워크 I/O 를 push
  // 전환 애니메이션과 겹쳐 진행시키고, 무거운 GoodsDetailContents 마운트 commit 과
  // 요청 burst 가 충돌하지 않게 한다. (내부 useConfigs() suspense 를 게이트 위에 유지)
  usePreloadQuery();

  const getDefaults = useCallback((): ILogging | undefined => {
    const detail = queryClient.getQueryData<GoodsDetailData>(
      queryKey.goodsDetail(goodsSno, fixedCouponSno),
    );
    const basic = queryClient.getQueryData<GoodsBasicData>(
      queryKey.goodsBasic(goodsSno, fixedCouponSno),
    );
    if (!detail || !basic) return undefined;

    const categories = basic.display_categories?.[0] ?? null;

    return {
      analytics: {
        ...(from != null ? { FROM: from } : {}),
        CATEGORY_DEPTH: categories?.depth,
        CATEGORY_SNO: categories?.sno.toString(),
        CATEGORY_NAME: categories?.name,
        DELIVERY_LEAD_TIME:
          basic.delivery_type_info.delivery_type === 'standard'
            ? detail.goods.send_expectation_info?.days
            : null,
        DELIVERY_TYPE: basic.delivery_type_info.delivery_type,
        DISCOUNT_RATE: basic.price_info.discount_rate,
        GOODS_SNO: basic.sno.toString(),
        GOODS_NAME: basic.name,
        IS_NEW: basic.is_new ? '1' : '0',
        LIKES_COUNT: detail.goods.score.likes_count,
        LIKES_COUNT_RANGE: getNumberRange({
          rangeUnit: 500,
          target: detail.goods.score.likes_count,
        }),
        MARKET_SNO: basic.market.sno.toString(),
        MARKET_NAME: basic.market.name,
        MARKET_TYPE: basic.market.market_vertical_type.name,
        ORIGINAL_PRICE: basic.price_info.consumer,
        REVIEW_COUNT: detail.goods.score.total_review_count,
        REVIEW_COUNT_RANGE: getNumberRange({
          target: detail.goods.score.total_review_count,
          rangeUnit: 50,
        }),
        REVIEW_RATING: basic.positive_review_rate,
        SALES_PRICE: basic.price_info.thumbnail_price,
        SALES_PRICE_RANGE: getNumberRange({
          target: basic.price_info.thumbnail_price,
          rangeUnit: 5000,
        }),
        STANDARD_CATEGORY_SNO: basic.standard_category.sno.toString(),
        STANDARD_CATEGORY_NAME: basic.standard_category.name,
        IS_AD: isNotNil(entryAd) ? '1' : '0',
        ...detail.logging.analytics,
      },
    };
  }, [queryClient, goodsSno, fixedCouponSno, entryAd, from]);

  return (
    <AnalyticsScreen screen="GOODS_DETAIL" getDefaults={getDefaults}>
      <GoodsDetailBody />
    </AnalyticsScreen>
  );
};

export default GoodsDetailPage;
